import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Input, Segmented, Select, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Play, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CLEANUP_KINDS,
  CleanupClient,
  type CleanupItemDTO,
  type CleanupKind,
  type CleanupRuleDTO,
  type CleanupSuggestionDTO,
} from "../../api/configurator";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import { OpenRecord } from "../../components/OpenRecord";
import { NexpimView } from "../../views";
import { COLOURS_KEY } from "./PartColoursTab";

const KEY = ["manufacturing", "cleanup"];

type View = "suggestions" | "rules";

export function cleanupKindLabel(kind: CleanupKind, t: (key: string, fallback: string) => string): string {
  switch (kind) {
    case "FINISH_DUPLICATE":
      return t("cleanup.kind.finish", "Colours spelt twice");
    case "PIECE_NAME":
      return t("cleanup.kind.piece-name", "Pieces named after another SKU");
    case "BAG":
      return t("cleanup.kind.bag", "Hardware bags");
    default:
      return t("cleanup.kind.guide", "Assembly guides");
  }
}

/**
 * The part and BOM cleanup (design §5). Suggestions come from the data:
 * colours spelt twice, pieces named after a SKU that does not use them, a
 * model's hardware bags and guides that can be one generic XX part. Accepting
 * one stores a rule and applies it at once; go-live applies every rule again
 * after the SIGM import (step 08e), which writes SIGM's names and bills back.
 * A refused suggestion does not come back.
 */
export function CleanupPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new CleanupClient(http), [http]);
  const allowed = hasView(NexpimView.MANUFACTURING_PARTS);
  const [view, setView] = useState<View>("suggestions");
  const title = t("navigation.item.engineering-cleanup", "Cleanup");

  const apply = useMutation({
    mutationFn: async () => {
      const preview = await client.apply(true);
      const changes = (preview.counts.names ?? 0) + (preview.counts.lines_replaced ?? 0);
      if (changes === 0 && preview.problems.length === 0) {
        toast.success(t("cleanup.apply.nothing", "Every rule holds already: nothing to change."));
        return null;
      }
      const ok = await confirm({
        title: t("cleanup.apply.title", "Apply every rule now?"),
        message: t("cleanup.apply.message", "{{names}} names, {{lines}} BOM lines replaced, {{added}} added beside them.", {
          names: preview.counts.names ?? 0,
          lines: preview.counts.lines_replaced ?? 0,
          added: preview.counts.lines_added ?? 0,
        }) + (preview.problems.length > 0 ? ` ${preview.problems.join(" · ")}` : ""),
        confirmLabel: t("cleanup.apply.confirm", "Apply"),
      });
      return ok ? client.apply(false) : null;
    },
    onSuccess: async (report) => {
      if (report) {
        toast.success(t("cleanup.apply.done", "Rules applied: {{lines}} BOM lines replaced.", { lines: report.counts.lines_replaced ?? 0 }));
        await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
      }
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <p className="p-4 text-[13px] text-ink-muted lg:p-6">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
      </>
    );
  }
  return (
    <>
      <PageHeader
        title={title}
        subtitle={t(
          "cleanup.subtitle",
          "Corrections kept as rules: accepted, a rule is applied at once and again after every SIGM import until cutover, since the import writes SIGM's names and bills back.",
        )}
        actions={
          canWrite ? (
            <Button variant="secondary" icon={<Play className="size-4" />} loading={apply.isPending} onClick={() => apply.mutate()}>
              {t("cleanup.apply", "Apply every rule")}
            </Button>
          ) : null
        }
      />
      <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-2 lg:px-6">
        <Segmented<View>
          value={view}
          onChange={setView}
          options={[
            { value: "suggestions", label: t("cleanup.view.suggestions", "Suggestions") },
            { value: "rules", label: t("cleanup.view.rules", "Rules") },
          ]}
        />
      </div>
      {view === "suggestions" ? <SuggestionsView /> : <RulesView />}
    </>
  );
}

function SuggestionsView() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new CleanupClient(http), [http]);
  const [kind, setKind] = useState<CleanupKind>("FINISH_DUPLICATE");
  const suggestions = useQuery({ queryKey: [...KEY, "suggestions"], queryFn: () => client.suggestions() });
  const all = suggestions.data ?? [];
  const shown = all.filter((one) => one.kind === kind);
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 lg:px-6">
        <Segmented<CleanupKind>
          value={kind}
          onChange={setKind}
          options={CLEANUP_KINDS.map((one) => ({
            value: one,
            label: `${cleanupKindLabel(one, t)} ${String(all.filter((s) => s.kind === one).length)}`,
          }))}
        />
      </div>
      <div className="flex flex-col gap-3 px-4 pb-6 lg:px-6">
        {suggestions.isLoading && <p className="text-[13px] text-ink-muted">{t("table.loading", "Loading")}</p>}
        {suggestions.data && shown.length === 0 && <p className="text-[13px] text-ink-muted">{t("cleanup.none", "Nothing to suggest here.")}</p>}
        {shown.map((suggestion) => (
          <SuggestionCard key={`${suggestion.kind}:${suggestion.subject}`} suggestion={suggestion} />
        ))}
      </div>
    </div>
  );
}

function SuggestionCard({ suggestion }: { suggestion: CleanupSuggestionDTO }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new CleanupClient(http), [http]);
  const [keep, setKeep] = useState(suggestion.proposed ?? "");
  const [name, setName] = useState(suggestion.proposed ?? "");
  const [showLeftOut, setShowLeftOut] = useState(false);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: KEY });
    await queryClient.invalidateQueries({ queryKey: COLOURS_KEY });
  };
  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const accept = useMutation({
    mutationFn: () => client.accept(suggestion.kind, suggestion.subject, suggestion.kind === "FINISH_DUPLICATE" ? { keep } : suggestion.kind === "PIECE_NAME" ? { name } : {}),
    onSuccess: async (report) => {
      toast.success(
        report.counts.lines_replaced
          ? t("cleanup.accepted.lines", "Done: {{n}} BOM lines now use the generic part.", { n: report.counts.lines_replaced })
          : t("cleanup.accepted", "Done."),
      );
      await refresh();
    },
    onError: fail,
  });
  const refuse = useMutation({ mutationFn: () => client.refuse(suggestion.kind, suggestion.subject), onSuccess: refresh, onError: fail });
  const busy = accept.isPending || refuse.isPending;

  return (
    <article className="rounded-md border border-line">
      <header className="flex flex-wrap items-baseline gap-2 border-b border-line px-3 py-2">
        <h3 className="text-[14px] font-semibold">{suggestion.title}</h3>
        <span className="text-[13px] text-ink-muted">{suggestion.detail}</span>
      </header>
      <div className="flex flex-col gap-3 px-3 py-2">
        {suggestion.generic && <ItemRow item={suggestion.generic} generic />}
        <ul className="flex flex-col gap-1">
          {suggestion.items.map((item) => (
            <ItemRow key={item.number} item={item} />
          ))}
        </ul>
        {suggestion.left_out.length > 0 && (
          <div>
            <button type="button" className="text-[13px] text-ink-muted underline" onClick={() => setShowLeftOut(!showLeftOut)}>
              {t("cleanup.left-out", "{{n}} left as they are", { n: suggestion.left_out.length })}
            </button>
            {showLeftOut && (
              <ul className="mt-1 flex flex-col gap-1 opacity-80">
                {suggestion.left_out.map((item) => (
                  <ItemRow key={item.number} item={item} />
                ))}
              </ul>
            )}
          </div>
        )}
        {canWrite && (
          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-2">
            {suggestion.kind === "FINISH_DUPLICATE" && (
              <label className="flex items-center gap-2 text-[13px]">
                <span>{t("cleanup.keep", "Keep")}</span>
                <Select className="w-56" options={suggestion.items.map((item) => ({ value: item.number, label: item.number }))} value={keep}
                  onChange={(event) => setKeep(event.target.value)} />
              </label>
            )}
            {suggestion.kind === "PIECE_NAME" && (
              <Input className="max-w-md" value={name} onChange={(event) => setName(event.target.value)} aria-label={t("cleanup.new-name", "New name")} />
            )}
            <Button size="sm" variant="primary" icon={<Check className="size-4" />} loading={accept.isPending} disabled={busy || (suggestion.kind === "PIECE_NAME" && name.trim() === "")}
              onClick={() => accept.mutate()}>
              {t("cleanup.accept", "Accept")}
            </Button>
            <Button size="sm" variant="ghost" icon={<X className="size-4" />} loading={refuse.isPending} disabled={busy} onClick={() => refuse.mutate()}>
              {t("cleanup.refuse", "Refuse")}
            </Button>
          </div>
        )}
      </div>
    </article>
  );
}

function ItemRow({ item, generic = false }: { item: CleanupItemDTO; generic?: boolean }) {
  const { t } = useTranslation();
  return (
    <li className="list-none text-[13px]">
      <div className="flex flex-wrap items-center gap-2">
        {item.part_id != null && <OpenRecord target={{ kind: "part", id: item.part_id, tab: generic ? "bom" : "where-used" }} label={item.number} />}
        <span className={generic ? "font-mono font-semibold" : "font-mono"}>{item.number}</span>
        <span className="min-w-0 truncate">{item.name}</span>
        {generic && <Badge size="sm" variant="info">{t("cleanup.generic", "Generic")}</Badge>}
        {item.note && <Badge size="sm" variant="neutral">{item.note}</Badge>}
        {item.users > 0 && <span className="text-ink-muted">{t("cleanup.users", "on {{n}} BOMs", { n: item.users })}</span>}
      </div>
      {item.lines.length > 0 && (
        <p className="ml-9 text-[12px] text-ink-muted">
          {item.lines.map((line) => `${formatNumber(line.quantity)} × ${line.number}`).join(" · ")}
        </p>
      )}
    </li>
  );
}

function RulesView() {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new CleanupClient(http), [http]);
  const rules = useQuery({ queryKey: [...KEY, "rules"], queryFn: () => client.rules() });
  const drop = useMutation({
    mutationFn: (rule: CleanupRuleDTO) => client.dropRule(rule.kind, rule.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  const kindLabel = (rule: CleanupRuleDTO) =>
    rule.kind === "NAME" ? t("cleanup.rule.name", "Name") : rule.kind === "CONSOLIDATION" ? t("cleanup.rule.consolidation", "Replaced") : t("cleanup.rule.refused", "Refused");
  const columns = useMemo(() => {
    const c = defineColumns<CleanupRuleDTO>();
    return [
      c.text("kind", (row) => kindLabel(row), { header: t("cleanup.rule.kind", "Rule"), priority: 1, filter: "select" }),
      c.text("part", (row) => row.part_number, { header: t("cleanup.rule.part", "Part"), priority: 1 }),
      c.text("part_name", (row) => row.part_name, { header: t("column.part.name", "Name"), priority: 2 }),
      c.text("value", (row) => (row.kind === "NAME" ? row.value ?? "" : row.kind === "CONSOLIDATION" ? `→ ${row.target_number ?? ""}` : ""), {
        header: t("cleanup.rule.becomes", "Becomes"),
        priority: 1,
      }),
      c.text("reason", (row) => row.reason ?? "", { header: t("cleanup.rule.reason", "Why"), priority: 3 }),
      c.text("created_at", (row) => row.created_at.slice(0, 10), { header: t("cleanup.rule.when", "When"), priority: 3 }),
    ];
    // kindLabel only reads t.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<CleanupRuleDTO>
        data={rules.data ?? []}
        columns={columns}
        loading={rules.isLoading}
        getRowId={(row) => `${row.kind}:${String(row.id)}`}
        stateKey="engineering.cleanup-rules"
        rowActions={(row) =>
          canWrite ? (
            <Button size="sm" variant="ghost" onClick={() => {
              void (async () => {
                const ok = await confirm({
                  title: t("cleanup.drop.title", "Drop this rule?"),
                  message: row.kind === "REFUSED"
                    ? t("cleanup.drop.refused", "The suggestion comes back.")
                    : t("cleanup.drop.message", "Nothing is put back now: the next SIGM import brings SIGM's name or part back."),
                  confirmLabel: t("cleanup.drop.confirm", "Drop"),
                  variant: "danger",
                });
                if (ok) drop.mutate(row);
              })();
            }}>
              {t("cleanup.drop", "Drop")}
            </Button>
          ) : null
        }
        emptyMessage={t("cleanup.rules.empty", "No rule yet: accept a suggestion.")}
      />
    </div>
  );
}
